import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  ArrowUpRight, Building2, Mountain, Building, Sparkles, ShieldCheck, Activity,
  Search, Target, ClipboardList, Mail, Webhook, FileBarChart, Check, ChevronDown,
  Waypoints, ScrollText, Radar,
} from "lucide-react";
import { LogoMarkStatic } from "@/components/LogoMark";
import SeoHead from "@/components/SeoHead";
import { FAQ } from "@/content/faq";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ASSET_TILES = [
  { title: "Businesses for Sale",    desc: "Owner-operated companies where the founder is winding down.",         icon: Building2, accent: "from-emerald-500/30" },
  { title: "Multifamily Properties", desc: "Small and mid-size apartments held by tired or absentee landlords.",  icon: Building,  accent: "from-cyan-500/30" },
  { title: "Land & Acreage",         desc: "Long-held raw land parcels where the owner is ready to close out.",   icon: Mountain,  accent: "from-amber-500/30" },
];

const STEPS = [
  { n: "01", icon: Search,        title: "Discover",      body: "Our discovery engine reads public records daily: business dissolutions, tax lien filings, and municipal code violations, and puts the freshest signals in front of you." },
  { n: "02", icon: Target,        title: "Score with AI", body: "Every opportunity is rated across six dimensions: Seller Motivation, Life Events, Receptivity, Deal Viability, Owner Accessibility, and Lead Freshness. You see the reasoning behind every score." },
  { n: "03", icon: ClipboardList, title: "Close faster",  body: "A ten-stage pipeline, automated outreach with compliance guardrails, ready-to-send LOIs and PSAs, and executive PDF reports for your partners and LPs." },
];

const FEATURES = [
  { icon: Sparkles,     title: "Acquisition Scoring",    body: "Every opportunity rated 0-100 across six dimensions. See exactly which signals fired and why." },
  { icon: Activity,     title: "Pipeline & CRM",         body: "A ten-stage pipeline that moves deals forward for you, from new lead to LOI to close. Tasks, notes, and a full audit trail on every deal." },
  { icon: FileBarChart, title: "Executive Reporting",    body: "Schedule weekly PDF digests for investors, partners, and LPs. CSV exports for your spreadsheets." },
  { icon: Mail,         title: "Automated Outreach",     body: "Outreach tools with pacing controls, sent from your own connected email account." },
  { icon: Webhook,      title: "Webhook & GraphQL API",  body: "Push deals from partner systems via webhook. Pull data into your own stack via GraphQL on the Business tier." },
  { icon: ShieldCheck,  title: "Compliance-First",       body: "Licensed data sources, public records, and your own imports. No scraping. No ToS bypasses. Full audit trail on every record." },
];

const SCENARIOS = [
  {
    icon: Building,
    title: "A 12-unit multifamily with an absentee, out-of-state owner",
    body: "Absentee owner. Held 27 years. Address of record is a different state than the property. The score rises sharply the day an enrichment signal fires, before any broker touches it.",
    tag: "Illustrative scenario",
  },
  {
    icon: Waypoints,
    title: "A business dissolution with a federal lien behind it",
    body: "Corporate dissolution recorded Monday. Federal tax lien lookup pulls a match by entity name. Compound signal fires: Dissolved + Federal Tax Lien. The owners are winding down under real pressure.",
    tag: "Illustrative scenario",
  },
  {
    icon: Radar,
    title: "A long-held land parcel with an out-of-state owner",
    body: "40 acres. Held 22 years. Owner address is out of state. When the score climbs, the opportunity moves up your ranked list so you can act first.",
    tag: "Illustrative scenario",
  },
];

const FAQ_LOCAL_MOVED_TO_CONTENT = null;

const TIERS = [
  {
    id: "free",
    name: "Free",
    price_m: 0, price_y: 0,
    tagline: "Kick the tires.",
    features: [
      "5 saved opportunities per month",
      "AI scoring visible (6 dimensions)",
      "Seller funnel access",
      "Weekly Wind-Down Digest available on upgrade",
    ],
    cta: "Start free",
    plan: "free",
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    price_m: 99, price_y: 990,
    tagline: "Serious acquirers.",
    features: [
      "Unlimited opportunities",
      "AI scoring (6 dimensions)",
      "Automated outreach sequences",
      "AI Assistant + document generator",
      "PDF & DOCX exports",
      "Weekly Wind-Down Digest (top 20 rows)",
    ],
    cta: "Start Pro",
    plan: "pro",
    highlight: true,
  },
  {
    id: "business",
    name: "Business",
    price_m: 299, price_y: 2990,
    tagline: "Funds & operators.",
    features: [
      "Everything in Pro",
      "Bulk CSV import",
      "Webhook ingestion",
      "GraphQL API",
      "Social automation on closed deals",
      "Weekly Wind-Down Digest (top 40 rows)",
    ],
    cta: "Start Business",
    plan: "business",
    highlight: false,
  },
];

export default function LandingPublic() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [stats, setStats] = useState(null);
  const [tierPrices, setTierPrices] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/stats`).then((r) => setStats(r.data)).catch(() => {});
    // Task 3: SoftwareApplication offers pull from the live billing tiers endpoint
    axios.get(`${BACKEND}/api/billing/tiers`).then((r) => setTierPrices(r.data)).catch(() => {});
  }, []);

  // JSON-LD structured data (Task 3): Organization + SoftwareApplication + FAQPage
  // FAQ payload derives from the imported FAQ constant ,  cannot drift from
  // the visible accordion since both read the same array.
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Acquigine",
      "url": (typeof window !== "undefined" ? window.location.origin : "https://acquigine.com"),
      "logo": (typeof window !== "undefined" ? `${window.location.origin}/images/acquigine-logo.png` : ""),
      "description":
        "Acquisition intelligence platform. AI-scored acquisition opportunities from public records: businesses, multifamily, and land.",
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Acquigine",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description":
        "AI-scored acquisition intelligence for businesses, multifamily properties, and land. Six-dimension scoring, outreach tools with pacing controls, ready-to-send LOIs.",
      // Live offers ,  pulled from GET /api/billing/tiers so pricing here
      // is always the actual pricing the checkout uses. Fall back to hard
      // pricing if the fetch hasn't returned yet (never fabricate).
      "offers": (tierPrices?.tiers || [
        { id: "free", name: "Free", monthly_price: 0 },
        { id: "pro", name: "Pro", monthly_price: 99 },
        { id: "business", name: "Business", monthly_price: 299 },
      ]).map((t) => ({
        "@type": "Offer",
        "name": t.name || (t.id === "free" ? "Free" : t.id === "pro" ? "Pro" : "Business"),
        "price": String(t.monthly_price ?? 0),
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": String(t.monthly_price ?? 0),
          "priceCurrency": "USD",
          "unitText": "MONTH",
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    },
  ];

  const fmtOpps = (n) => {
    if (!n) return "…";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M+`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}k+`;
    return `${n}`;
  };

  const statTiles = [
    { v: fmtOpps(stats?.opportunities_indexed),                                l: "Opportunities indexed" },
    { v: stats ? `${stats.data_sources}` : "…",                                  l: "Live data sources monitored" },
    { v: "6 dimensions",                                                         l: "Scoring dimensions" },
  ];

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100 relative overflow-x-clip" data-testid="landing-public">
      <SeoHead route="/" jsonLd={jsonLd} />
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-1/3 h-[420px] w-[720px] rounded-full bg-emerald-500/10 blur-[140px] pointer-events-none" />

      {/* Nav */}
      <nav className="sticky top-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="public-home-link">
            <LogoMarkStatic className="h-20 md:h-[104px]" />
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm text-slate-400">
            <a href="#how" className="hover:text-slate-100 transition-colors" data-testid="nav-how">How it works</a>
            <a href="#features" className="hover:text-slate-100 transition-colors" data-testid="nav-features">Features</a>
            <a href="#pricing" className="hover:text-slate-100 transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#faq" className="hover:text-slate-100 transition-colors" data-testid="nav-faq">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/sell" data-testid="public-sell-link" className="hidden sm:inline text-sm text-slate-300 hover:text-emerald-400 transition-colors">For Sellers</Link>
            <Link to="/login" data-testid="public-login-link" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Sign in</Link>
            <Link to="/register?plan=free" data-testid="public-register-link" className="btn-primary text-sm">Start Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-20">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 fade-in-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-400 mb-6">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 pulse-dot" />
              Acquisition Intelligence · United States
            </div>
            <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Find more deals.<br />
              <span className="text-emerald-400">Structure smarter.</span><br />
              Close faster.
            </h1>
            <p className="mt-7 text-lg text-slate-400 max-w-2xl leading-relaxed">
              Acquigine reads thousands of public records daily to find business owners,
              landlords, and landowners
              <span className="text-slate-200"> most likely to say yes to seller financing, lease options, and creative structures.</span>
              You get a ranked list of opportunities in businesses, multifamily, and land, scored and enriched, with the reasoning behind every score.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/register?plan=free" data-testid="hero-cta-register" className="btn-primary text-base inline-flex items-center gap-2">
                Find My First Deal Free <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href="#how" className="btn-ghost text-sm" data-testid="hero-cta-how">See How the Score Works</a>
              <Link to="/pricing" className="btn-ghost text-sm" data-testid="hero-cta-pricing">Pricing</Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-400" /> Free forever tier</span>
              <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-400" /> No scraping · Licensed sources only</span>
              <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-400" /> Cancel anytime</span>
            </div>
          </div>

          <div className="md:col-span-4 fade-in-up stagger-2">
            <div className="ae-card p-6" data-testid="hero-score-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">CF Readiness Score</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400 mt-1">First Pass</div>
                </div>
                <div className="text-xs text-slate-400 text-right">12-unit Multifamily</div>
              </div>
              <div className="flex items-baseline gap-2 border-t border-white/8 pt-5">
                <span className="font-heading font-bold text-6xl text-emerald-400 font-mono tabular-nums leading-none" data-testid="hero-score-value">
                  87
                </span>
                <span className="text-lg text-slate-500 font-mono">/100</span>
                <span
                  className="ml-auto text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-2 py-1"
                  data-testid="hero-score-tier"
                >
                  Strong fit
                </span>
              </div>
              <div className="mt-3 h-1.5 w-full rounded-full bg-white/8 overflow-hidden" data-testid="hero-score-progress-track">
                <div
                  className="h-full rounded-full bg-emerald-400"
                  style={{ width: "87%" }}
                  data-testid="hero-score-progress-fill"
                />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-slate-400" data-testid="hero-score-subscores">
                <div className="flex items-center justify-between"><span>Seller Motivation</span><span className="font-mono text-emerald-400">92</span></div>
                <div className="flex items-center justify-between"><span>Life Events</span><span className="font-mono text-emerald-400">90</span></div>
                <div className="flex items-center justify-between"><span>Receptivity</span><span className="font-mono text-emerald-400">85</span></div>
                <div className="flex items-center justify-between"><span>Deal Viability</span><span className="font-mono text-emerald-400">88</span></div>
                <div className="flex items-center justify-between"><span>Owner Accessibility</span><span className="font-mono text-emerald-400">83</span></div>
                <div className="flex items-center justify-between"><span>Lead Freshness</span><span className="font-mono text-emerald-400">86</span></div>
              </div>
              <div className="mt-4 pt-3 border-t border-white/8 text-[11px] text-slate-500 leading-relaxed">
                Absentee owner · Tax delinquency on record · Owned 27 yrs.
              </div>
            </div>
            <div className="mt-3 px-3 py-2 rounded-sm bg-amber-500/10 border border-amber-500/30 text-center" data-testid="hero-score-disclaimer">
              <span className="text-xs font-medium text-amber-300">Illustrative example, not a real record</span>
            </div>
          </div>
        </div>

        {/* Asset tiles */}
        <div className="mt-20 grid md:grid-cols-3 gap-4" data-testid="asset-tiles">
          {ASSET_TILES.map((t, i) => {
            const I = t.icon;
            return (
              <div key={t.title} className={`ae-card p-6 fade-in-up stagger-${i + 1} relative overflow-hidden`}>
                <div className={`absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gradient-to-br ${t.accent} to-transparent blur-2xl`} />
                <I className="h-7 w-7 text-emerald-400 mb-4" strokeWidth={1.5} />
                <div className="font-heading text-xl">{t.title}</div>
                <div className="text-xs text-slate-500 mt-1.5">{t.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats band */}
      <section className="relative z-10 border-y border-white/8 bg-[#08090E]" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {statTiles.map((s) => (
            <div key={s.l} className="text-center md:text-left">
              <div className="font-heading text-3xl sm:text-4xl font-bold text-emerald-400 font-mono tabular-nums">{s.v}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1.5">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">How it works</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight max-w-3xl">From signal to signed LOI in three steps.</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-px bg-white/8 border border-white/8">
            {STEPS.map((s) => {
              const I = s.icon;
              return (
                <div key={s.n} className="bg-[#0A0D12] p-8 relative">
                  <div className="absolute top-6 right-6 font-mono text-xs text-emerald-400/40 tracking-widest">{s.n}</div>
                  <I className="h-8 w-8 text-emerald-400 mb-5" strokeWidth={1.5} />
                  <div className="font-heading text-xl">{s.title}</div>
                  <div className="text-sm text-slate-400 mt-2.5 leading-relaxed">{s.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="relative z-10 border-t border-white/8 bg-[#0A0D12]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">Platform Capabilities</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight max-w-3xl">A complete acquisition engine, from discovery to close.</h2>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-white/8 border border-white/8">
            {FEATURES.map((f) => {
              const I = f.icon;
              return (
                <div key={f.title} className="bg-[#0A0D12] p-8 hover:bg-[#0F1218] transition-colors duration-200">
                  <I className="h-6 w-6 text-emerald-400 mb-4" strokeWidth={1.5} />
                  <div className="font-heading text-lg">{f.title}</div>
                  <div className="text-sm text-slate-400 mt-2 leading-relaxed">{f.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 border-t border-white/8" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">Pricing</div>
              <h2 className="font-heading text-4xl sm:text-5xl tracking-tight max-w-2xl">Start free. Scale when you close.</h2>
              <p className="text-slate-400 mt-3 text-sm">All tiers include the seller funnel and lead capture. Cancel anytime.</p>
            </div>
            <div className="inline-flex items-center bg-[#0A0D12] border border-white/8 rounded-sm p-1 self-start sm:self-auto" data-testid="pricing-toggle">
              <button
                onClick={() => setAnnual(false)}
                data-testid="pricing-monthly"
                className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-sm transition-colors ${!annual ? "bg-emerald-500 text-black" : "text-slate-400 hover:text-slate-100"}`}
              >Monthly</button>
              <button
                onClick={() => setAnnual(true)}
                data-testid="pricing-annual"
                className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-sm transition-colors ${annual ? "bg-emerald-500 text-black" : "text-slate-400 hover:text-slate-100"}`}
              >Annual <span className="text-[10px] opacity-80 ml-1">save 17%</span></button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {TIERS.map((t) => {
              const price = annual ? t.price_y : t.price_m;
              const suffix = annual ? "/yr" : "/mo";
              return (
                <div
                  key={t.name}
                  data-testid={`tier-${t.id}`}
                  className={`relative ae-card p-7 flex flex-col ${t.highlight ? "border-emerald-500/40 ring-1 ring-emerald-500/20" : ""}`}
                >
                  {t.highlight && (
                    <div className="absolute -top-3 left-7 px-2 py-0.5 bg-emerald-500 text-black text-[10px] uppercase tracking-widest font-mono font-bold">Most popular</div>
                  )}
                  <div className="font-heading text-lg">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.tagline}</div>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-bold text-slate-100 font-mono tabular-nums">${price}</span>
                    <span className="text-xs text-slate-500">{suffix}</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm text-slate-300 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/register?plan=${t.plan}`}
                    data-testid={`tier-cta-${t.id}`}
                    className={`mt-7 text-center text-sm py-2.5 rounded-sm transition-colors ${t.highlight ? "btn-primary" : "btn-ghost"}`}
                  >
                    {t.cta}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scenarios (replaces testimonials) */}
      <section className="relative z-10 border-t border-white/8 bg-[#0A0D12]" data-testid="scenarios-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">What this looks like in practice</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight max-w-3xl">Three hypothetical scenarios drawn from real signal patterns.</h2>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            These are illustrative examples, not customer testimonials. Every claim maps to a signal our scoring engine actually reads.
          </p>
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {SCENARIOS.map((s, i) => {
              const I = s.icon;
              return (
                <div key={i} data-testid={`scenario-${i}`} className="ae-card p-7 flex flex-col">
                  <I className="h-6 w-6 text-emerald-400/80 mb-4" strokeWidth={1.5} />
                  <div className="font-heading text-lg leading-snug">{s.title}</div>
                  <p className="text-sm text-slate-400 leading-relaxed mt-3 flex-1">{s.body}</p>
                  <div className="mt-5 pt-4 border-t border-white/8">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                      <ScrollText className="h-3 w-3" /> {s.tag}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 border-t border-white/8" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">FAQ</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">Questions, answered.</h2>
          <div className="mt-12 divide-y divide-white/8 border-y border-white/8">
            {FAQ.map((item, i) => {
              const open = openFaq === i;
              return (
                <button
                  key={item.q}
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  data-testid={`faq-item-${i}`}
                  className="w-full text-left py-6 group transition-colors"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="font-heading text-lg text-slate-100 group-hover:text-emerald-400 transition-colors">{item.q}</div>
                    <ChevronDown className={`h-5 w-5 text-slate-500 shrink-0 transition-transform duration-200 ${open ? "rotate-180 text-emerald-400" : ""}`} />
                  </div>
                  <div
                    className="grid transition-all duration-300 ease-out"
                    style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}
                  >
                    <div className="overflow-hidden">
                      <p className="text-sm text-slate-400 mt-4 leading-relaxed">
                        {item.aLink ? (
                          <>
                            {item.a.split(item.aLink.text)[0]}
                            <Link to={item.aLink.to} className="text-emerald-400 hover:underline" data-testid={`faq-inline-link-${i}`}>{item.aLink.text}</Link>
                            {item.a.split(item.aLink.text)[1]}
                          </>
                        ) : item.a}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 border-t border-white/8 bg-gradient-to-b from-[#05070A] to-[#0A0D12]" data-testid="final-cta">
        <div className="max-w-5xl mx-auto px-6 py-28 text-center">
          <h2 className="font-heading text-4xl sm:text-6xl tracking-tighter leading-[1]">
            Your next acquisition is<br />
            <span className="text-emerald-400">already in our index.</span>
          </h2>
          <p className="text-slate-400 mt-6 max-w-2xl mx-auto">
            Spin up your free account in 60 seconds. Score your first 5 deals tonight.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3">
            <Link to="/register?plan=free" data-testid="bottom-cta-register" className="btn-primary inline-flex items-center gap-2 text-base">
              Start free · No credit card <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/login" data-testid="bottom-cta-login" className="btn-ghost text-sm">Sign in</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/8 bg-[#05070A]" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid sm:grid-cols-2 md:grid-cols-4 gap-10">
          <div>
            <LogoMarkStatic className="h-20 md:h-[120px]" />
            <p className="text-xs text-slate-500 mt-4 leading-relaxed max-w-xs">
              Compliance-first acquisition intelligence for serious operators.
            </p>
          </div>
          <FooterCol title="Product" links={[
            { l: "How it works", h: "#how" },
            { l: "Features", h: "#features" },
            { l: "Pricing", to: "/pricing" },
            { l: "For Sellers", to: "/sell" },
          ]} />
          <FooterCol title="Company" links={[
            { l: "FAQ", h: "#faq" },
            { l: "Sign in", to: "/login" },
            { l: "Start free", to: "/register?plan=free" },
          ]} />
          <FooterCol title="Legal" links={[
            { l: "Privacy", to: "/privacy" },
            { l: "Terms", to: "/terms" },
            { l: "Compliance", to: "/compliance" },
            { l: "Sender Agreement", to: "/sender-agreement" },
          ]} />
        </div>
        <div className="border-t border-white/8 py-6 text-center text-xs text-slate-600">
          © {new Date().getFullYear()} Acquigine · Find more deals. Structure smarter. Close faster.
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-4">{title}</div>
      <ul className="space-y-2.5 text-sm">
        {links.map((lnk) => (
          <li key={lnk.l}>
            {lnk.to ? (
              <Link to={lnk.to} className="text-slate-300 hover:text-emerald-400 transition-colors" data-testid={`footer-link-${lnk.l.toLowerCase().replace(/\s+/g, '-')}`}>{lnk.l}</Link>
            ) : (
              <a href={lnk.h} className="text-slate-300 hover:text-emerald-400 transition-colors" data-testid={`footer-link-${lnk.l.toLowerCase().replace(/\s+/g, '-')}`}>{lnk.l}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
